import React, { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Plus, Download, FileText, Trash2, Send, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function Financing() {
  return (
    <div className="space-y-5" data-testid="financing-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Books</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Financing</h1>
        <p className="text-sm text-muted-foreground mt-1">Credits, debits, vendor & customer balances, expenses and monthly reports.</p>
      </div>

      <Tabs defaultValue="ledger">
        <TabsList>
          <TabsTrigger value="ledger" data-testid="tab-fin-ledger">Ledger</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-fin-vendors">Vendor Credits</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-fin-customers">Customer Credits</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-fin-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-fin-report">Monthly Report</TabsTrigger>
        </TabsList>

        <TabsContent value="ledger" className="mt-4"><Ledger /></TabsContent>
        <TabsContent value="vendors" className="mt-4"><VendorCredits /></TabsContent>
        <TabsContent value="customers" className="mt-4"><CustomerCredits /></TabsContent>
        <TabsContent value="expenses" className="mt-4"><Expenses /></TabsContent>
        <TabsContent value="report" className="mt-4"><MonthlyReport /></TabsContent>
      </Tabs>
    </div>
  );
}

function todayStr() { return new Date().toISOString().slice(0, 10); }
function monthStartStr() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function Ledger() {
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/finance/ledger", { params: { from_date: from, to_date: to } });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4">
      <Card className="p-4 flex flex-wrap items-end gap-3" data-testid="ledger-filters">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="ledger-from" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="ledger-to" />
        </div>
        <Button onClick={load} disabled={busy} data-testid="ledger-refresh">{busy ? "Loading…" : "Refresh"}</Button>
      </Card>

      {data && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard label="Income (paid)" value={inr(data.summary.income_paid)} color="emerald" icon={<ArrowDownRight />} />
            <SummaryCard label="Expenses (paid)" value={inr(data.summary.expense_paid)} color="rose" icon={<ArrowUpRight />} />
            <SummaryCard label="Customers owe us" value={inr(data.summary.customer_owes_us)} color="amber" />
            <SummaryCard label="We owe vendors" value={inr(data.summary.we_owe_vendors)} color="amber" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <SummaryCard label="Net cash (in − out)" value={inr(data.summary.net_cash)} color={data.summary.net_cash >= 0 ? "emerald" : "rose"} />
            <SummaryCard label="Income on credit (unpaid)" value={inr(data.summary.income_credit_outstanding)} color="amber" />
            <SummaryCard label="Expenses on credit" value={inr(data.summary.expense_credit_outstanding)} color="amber" />
          </div>

          <Card className="p-4" data-testid="ledger-table-card">
            <div className="text-sm text-muted-foreground mb-2">{data.entries.length} entries</div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Ref #</TableHead>
                <TableHead>Party</TableHead>
                <TableHead className="text-right">Credit (+)</TableHead>
                <TableHead className="text-right">Debit (−)</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.entries.map(e => (
                  <TableRow key={e.id} data-testid={`ledger-row-${e.id}`}>
                    <TableCell className="font-mono text-xs">{e.date}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{e.type}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{e.ref_no}</TableCell>
                    <TableCell className="text-sm">{e.party || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{e.credit ? inr(e.credit) : ""}</TableCell>
                    <TableCell className="text-right font-mono text-rose-700">{e.debit ? inr(e.debit) : ""}</TableCell>
                    <TableCell className="text-right font-mono">{e.balance ? inr(e.balance) : ""}</TableCell>
                    <TableCell><Badge variant={e.status === "paid" || e.status === "settled" ? "secondary" : "destructive"} className="text-xs">{e.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {data.entries.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No entries in range.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color = "muted", icon }) {
  const palette = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    rose: "border-rose-200 bg-rose-50 text-rose-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    muted: "border-border bg-card text-foreground",
  }[color] || "border-border bg-card";
  return (
    <Card className={`p-4 border ${palette}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest opacity-80">{label}</div>
        {icon}
      </div>
      <div className="font-heading text-2xl font-bold mt-1">{value}</div>
    </Card>
  );
}

function VendorCredits() {
  const [data, setData] = useState({ items: [], outstanding: 0 });
  const [open, setOpen] = useState(false);
  const [settleFor, setSettleFor] = useState(null);
  const [suppliers, setSuppliers] = useState([]);

  const load = () => api.get("/finance/vendor-credits").then(r => setData(r.data));
  useEffect(() => {
    load();
    api.get("/suppliers").then(r => setSuppliers(r.data)).catch(() => {});
  }, []);

  const del = async (vc) => {
    if (!window.confirm(`Delete vendor credit ${vc.credit_no}?`)) return;
    try { await api.delete(`/finance/vendor-credits/${vc.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <Card className="p-4 flex items-center gap-3 flex-wrap">
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5">
          <div className="text-[10px] uppercase tracking-widest text-amber-900">We owe vendors</div>
          <div className="font-heading text-2xl font-bold text-amber-900" data-testid="vendor-outstanding">{inr(data.outstanding)}</div>
        </div>
        <Button className="ml-auto" onClick={() => setOpen(true)} data-testid="add-vendor-credit"><Plus className="w-4 h-4 mr-1" />Record vendor credit</Button>
      </Card>

      <Card className="p-4">
        <Table>
          <TableHeader><TableRow>
            <TableHead>VC #</TableHead>
            <TableHead>Vendor</TableHead>
            <TableHead>Due</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Paid</TableHead>
            <TableHead className="text-right">Balance</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map(vc => (
              <TableRow key={vc.id} data-testid={`vendor-credit-${vc.id}`}>
                <TableCell className="font-mono text-xs">{vc.credit_no}</TableCell>
                <TableCell>{vc.supplier_name}</TableCell>
                <TableCell className="text-xs">{vc.due_date || "—"}</TableCell>
                <TableCell className="text-right font-mono">{inr(vc.amount)}</TableCell>
                <TableCell className="text-right font-mono">{inr(vc.paid_amount)}</TableCell>
                <TableCell className="text-right font-mono">{inr(vc.balance)}</TableCell>
                <TableCell><Badge variant={vc.status === "settled" ? "secondary" : "destructive"} className="text-xs">{vc.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {(vc.status === "pending" || vc.status === "partial") && (
                    <Button variant="outline" size="sm" onClick={() => setSettleFor(vc)} data-testid={`vendor-settle-${vc.id}`}><Send className="w-3 h-3 mr-1" />Settle</Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => del(vc)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No vendor credits recorded.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <NewVendorCreditDialog open={open} onOpenChange={setOpen} suppliers={suppliers} onSaved={load} />
      <SettleVendorDialog vc={settleFor} onClose={() => { setSettleFor(null); load(); }} />
    </div>
  );
}

function NewVendorCreditDialog({ open, onOpenChange, suppliers, onSaved }) {
  const [f, setF] = useState({ supplier_id: "", supplier_name: "", amount: "", note: "", due_date: "", bill_reference: "" });

  useEffect(() => { if (open) setF({ supplier_id: "", supplier_name: "", amount: "", note: "", due_date: "", bill_reference: "" }); }, [open]);

  const pickSupplier = (id) => {
    const s = suppliers.find(x => x.id === id);
    setF(prev => ({ ...prev, supplier_id: id, supplier_name: s?.name || prev.supplier_name }));
  };

  const submit = async () => {
    const amt = parseFloat(f.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!f.supplier_name) { toast.error("Vendor name required"); return; }
    try {
      await api.post("/finance/vendor-credits", { ...f, amount: amt });
      toast.success("Vendor credit recorded");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="vendor-credit-dialog">
        <DialogHeader><DialogTitle>Record vendor credit</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Pick supplier (or type below)</Label>
          <Select value={f.supplier_id || "_"} onValueChange={v => v !== "_" && pickSupplier(v)}>
            <SelectTrigger data-testid="vc-supplier-select"><SelectValue placeholder="Existing supplier…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_">—</SelectItem>
              {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Label className="text-xs">Vendor name *</Label>
          <Input value={f.supplier_name} onChange={e => setF(p => ({ ...p, supplier_name: e.target.value, supplier_id: "" }))} data-testid="vc-supplier-name" />
          <Label className="text-xs">Amount (₹) *</Label>
          <Input type="number" value={f.amount} onChange={e => setF(p => ({ ...p, amount: e.target.value }))} data-testid="vc-amount" />
          <Label className="text-xs">Due date</Label>
          <Input type="date" value={f.due_date} onChange={e => setF(p => ({ ...p, due_date: e.target.value }))} data-testid="vc-due" />
          <Label className="text-xs">Bill reference</Label>
          <Input value={f.bill_reference} onChange={e => setF(p => ({ ...p, bill_reference: e.target.value }))} />
          <Label className="text-xs">Note</Label>
          <Input value={f.note} onChange={e => setF(p => ({ ...p, note: e.target.value }))} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="vc-save">Record</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettleVendorDialog({ vc, onClose }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");

  useEffect(() => { if (vc) { setAmount(vc.balance?.toFixed(2) || ""); setMethod("cash"); setNote(""); } }, [vc]);
  if (!vc) return null;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    try {
      await api.post(`/finance/vendor-credits/${vc.id}/settle`, { amount: amt, payment_method: method, note });
      toast.success(`Paid ${inr(amt)} to ${vc.supplier_name}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={!!vc} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="vendor-settle-dialog">
        <DialogHeader><DialogTitle>Settle {vc.credit_no} · Balance {inr(vc.balance)}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <Label className="text-xs">Amount paid (₹) *</Label>
          <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} data-testid="vendor-settle-amount" />
          <Label className="text-xs">Payment method</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="bank">Bank transfer</SelectItem>
              <SelectItem value="upi">UPI</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
              <SelectItem value="card">Card</SelectItem>
            </SelectContent>
          </Select>
          <Label className="text-xs">Note</Label>
          <Input value={note} onChange={e => setNote(e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="vendor-settle-save"><Send className="w-4 h-4 mr-1" />Settle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomerCredits() {
  const [data, setData] = useState({ items: [], outstanding: 0 });
  const [status, setStatus] = useState("");
  useEffect(() => {
    const params = status ? { status } : {};
    api.get("/finance/customer-credits", { params }).then(r => setData(r.data));
  }, [status]);

  return (
    <div className="space-y-3">
      <Card className="p-4 flex items-center gap-3 flex-wrap" data-testid="customer-credits-summary">
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5">
          <div className="text-[10px] uppercase tracking-widest text-amber-900">Customers owe us</div>
          <div className="font-heading text-2xl font-bold text-amber-900">{inr(data.outstanding)}</div>
        </div>
        <Select value={status || "all"} onValueChange={v => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 ml-auto"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="settled">Settled</SelectItem>
          </SelectContent>
        </Select>
      </Card>
      <Card className="p-4">
        <Table>
          <TableHeader><TableRow>
            <TableHead>CN #</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Invoice</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Paid</TableHead>
            <TableHead className="text-right">Balance</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Issued</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map(cn => (
              <TableRow key={cn.id} data-testid={`cc-${cn.id}`}>
                <TableCell className="font-mono text-xs">{cn.credit_no}</TableCell>
                <TableCell>
                  <div className="text-sm">{cn.customer_name || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{cn.customer_mobile || ""}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{cn.invoice_no || "—"}</TableCell>
                <TableCell className="text-right font-mono">{inr(cn.amount)}</TableCell>
                <TableCell className="text-right font-mono">{inr(cn.paid_amount)}</TableCell>
                <TableCell className="text-right font-mono">{inr(cn.balance)}</TableCell>
                <TableCell><Badge variant={cn.status === "settled" ? "secondary" : "destructive"} className="text-xs">{cn.status}</Badge></TableCell>
                <TableCell className="text-xs">{new Date(cn.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No customer credit notes.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function Expenses() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "", amount: "", date: todayStr(), payee: "", payment_method: "cash", notes: "", paid: true, expense_type: "general" });

  const load = () => api.get("/expenses").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const amt = parseFloat(f.amount);
    if (!f.category || !amt || amt <= 0) { toast.error("Category and amount are required"); return; }
    try {
      await api.post("/expenses", { ...f, amount: amt });
      toast.success("Expense saved");
      setOpen(false); setF({ category: "", amount: "", date: todayStr(), payee: "", payment_method: "cash", notes: "", paid: true, expense_type: "general" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await api.delete(`/expenses/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="space-y-3">
      <Card className="p-4 flex items-center gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total spend</div>
          <div className="font-heading text-2xl font-bold">{inr(total)}</div>
        </div>
        <Button className="ml-auto" onClick={() => setOpen(true)} data-testid="add-expense-btn"><Plus className="w-4 h-4 mr-1" />New expense</Button>
      </Card>
      <Card className="p-4">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Payee</TableHead>
            <TableHead>Method</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} data-testid={`expense-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.date}</TableCell>
                <TableCell>
                  {r.expense_type === "parlour" ? (
                    <Badge variant="secondary" className="text-[10px]">parlour</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">general</Badge>
                  )}
                </TableCell>
                <TableCell>{r.category}</TableCell>
                <TableCell>{r.payee || "—"}</TableCell>
                <TableCell className="text-xs">{r.payment_method}</TableCell>
                <TableCell className="text-right font-mono text-rose-700">{inr(r.amount)}</TableCell>
                <TableCell><Badge variant={r.paid ? "secondary" : "destructive"} className="text-xs">{r.paid ? "paid" : "unpaid"}</Badge></TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => del(r.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">No expenses yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>New expense</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Type</Label>
            <Select value={f.expense_type} onValueChange={v => setF(p => ({ ...p, expense_type: v }))}>
              <SelectTrigger data-testid="expense-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General (rent, utilities, marketing…)</SelectItem>
                <SelectItem value="parlour">Parlour / Grooming (consumables, shampoo, supplies moved to parlour)</SelectItem>
              </SelectContent>
            </Select>
            <Label className="text-xs">Category *</Label>
            <Input value={f.category} onChange={e => setF(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Rent, Utilities, Supplies" data-testid="expense-category" />
            <Label className="text-xs">Amount (₹) *</Label>
            <Input type="number" value={f.amount} onChange={e => setF(p => ({ ...p, amount: e.target.value }))} data-testid="expense-amount" />
            <Label className="text-xs">Date</Label>
            <Input type="date" value={f.date} onChange={e => setF(p => ({ ...p, date: e.target.value }))} />
            <Label className="text-xs">Payee</Label>
            <Input value={f.payee} onChange={e => setF(p => ({ ...p, payee: e.target.value }))} />
            <Label className="text-xs">Method</Label>
            <Select value={f.payment_method} onValueChange={v => setF(p => ({ ...p, payment_method: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank">Bank</SelectItem>
                <SelectItem value="upi">UPI</SelectItem>
                <SelectItem value="card">Card</SelectItem>
                <SelectItem value="cheque">Cheque</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm pt-1">
              <input type="checkbox" checked={f.paid} onChange={e => setF(p => ({ ...p, paid: e.target.checked }))} data-testid="expense-paid" />
              Paid (uncheck to track as credit / pay-later)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="expense-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MonthlyReport() {
  const now = new Date();
  const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(ym);

  const download = () => {
    const token = localStorage.getItem("token");
    const branch = localStorage.getItem("currentBranchId");
    const url = `${API}/finance/monthly-report?month=${month}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}`, "X-Branch-Id": branch || "" } })
      .then(r => {
        if (!r.ok) throw new Error("Download failed");
        return r.blob();
      })
      .then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `furmigo-finance-${month}.csv`;
        a.click();
      })
      .catch(() => toast.error("Failed to download report"));
  };

  return (
    <Card className="p-6 max-w-xl space-y-4" data-testid="monthly-report-card">
      <div className="flex items-start gap-3">
        <FileText className="w-6 h-6 text-primary" />
        <div>
          <div className="font-heading text-lg font-bold">Monthly Financial Report</div>
          <div className="text-xs text-muted-foreground">Download a CSV with summary + full ledger for the selected month.</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 items-end">
        <div>
          <Label className="text-xs">Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="report-month" />
        </div>
        <Button onClick={download} data-testid="report-download"><Download className="w-4 h-4 mr-1" />Download CSV</Button>
      </div>
    </Card>
  );
}
